import { motion } from 'framer-motion';

const articles = [
  {
    category: 'Problem Philosophy',
    title: 'Why Solutions Are Overrated: A Manifesto',
    excerpt: 'In a world obsessed with solving problems, we explore the profound value of simply identifying and preserving them for future contemplation.',
    author: 'Dr. Marcus Thornwell',
    date: 'March 8, 2025',
    readTime: '12 min read',
    image: 'https://public.readdy.ai/ai/img_res/c3a1388f76f0f4d73e4323cf3cc844ff.jpg'
  },
  {
    category: 'Case Studies',
    title: 'The 847-Page Report: A Deep Dive into Office Chair Acoustics',
    excerpt: 'How we identified, documented, and preserved 47 distinct squeaking patterns in a single conference room chair over six months.',
    author: 'Sarah Chen, CPAS',
    date: 'March 5, 2025',
    readTime: '18 min read',
    image: 'https://readdy.ai/api/search-image?query=close-up%20of%20vintage%20typewriter%20with%20paper%20dramatic%20lighting%20moody%20atmosphere%20professional%20photography%20artistic%20composition%20writing%20and%20documentation%20concept&width=800&height=600&seq=sol-blog-002&orientation=landscape'
  },
  {
    category: 'Methodology',
    title: 'The 47-Point Assessment: Origins and Evolution',
    excerpt: 'Discover the rigorous development process behind our proprietary problem identification framework and its revolutionary impact.',
    author: 'Dr. Marcus Thornwell',
    date: 'March 1, 2025',
    readTime: '15 min read',
    image: 'https://readdy.ai/api/search-image?query=complex%20flowchart%20or%20diagram%20on%20whiteboard%20business%20strategy%20planning%20professional%20office%20setting%20natural%20lighting%20analytical%20thinking%20concept%20photography&width=800&height=600&seq=sol-blog-003&orientation=landscape'
  },
  {
    category: 'Client Success',
    title: 'From Ignorance to Awareness: Jennifer\'s Coffee Temperature Journey',
    excerpt: 'A comprehensive look at how one small business owner achieved complete awareness of her coffee machine\'s 2-degree temperature variance.',
    author: 'Michael Rodriguez, MPEP',
    date: 'February 28, 2025',
    readTime: '10 min read',
    image: 'https://readdy.ai/api/search-image?query=steaming%20coffee%20cup%20on%20wooden%20table%20morning%20light%20through%20window%20cozy%20atmosphere%20professional%20food%20photography%20warm%20tones%20peaceful%20moment&width=800&height=600&seq=sol-blog-004&orientation=landscape'
  },
  {
    category: 'Industry Insights',
    title: 'The Problem Preservation Movement: A Global Perspective',
    excerpt: 'How organizations worldwide are embracing the philosophy that properly documented problems are more valuable than hasty solutions.',
    author: 'Dr. Marcus Thornwell',
    date: 'February 25, 2025',
    readTime: '20 min read',
    image: 'https://readdy.ai/api/search-image?query=world%20map%20with%20glowing%20connection%20points%20global%20network%20concept%20professional%20business%20photography%20modern%20technology%20visualization%20dark%20background%20with%20golden%20lights&width=800&height=600&seq=sol-blog-005&orientation=landscape'
  },
  {
    category: 'Techniques',
    title: 'Advanced Escalation Pathways: When to Elevate Your Concerns',
    excerpt: 'Master the art of strategic problem escalation with our comprehensive guide to multi-tier notification protocols.',
    author: 'Emily Watson, CPAS',
    date: 'February 22, 2025',
    readTime: '14 min read',
    image: 'https://readdy.ai/api/search-image?query=ascending%20staircase%20or%20steps%20leading%20upward%20dramatic%20perspective%20architectural%20photography%20natural%20light%20progress%20and%20growth%20concept%20professional%20quality&width=800&height=600&seq=sol-blog-006&orientation=landscape'
  }
];

const BlogPreview = () => {
  return (
    <section className="relative py-32 bg-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-center justify-center gap-3 mb-6"
          >
            <div className="w-12 h-[2px] bg-gold"></div>
            <span className="text-gold text-sm font-semibold tracking-widest uppercase">
              Insights & Research
            </span>
            <div className="w-12 h-[2px] bg-gold"></div>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-6xl font-black text-white mb-6"
          >
            Latest <span className="text-gold">Thought Leadership</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl text-cream/70 max-w-3xl mx-auto"
          >
            Explore our comprehensive research, case studies, and philosophical insights 
            into the art and science of problem management.
          </motion.p>
        </div>

        {/* Articles Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {articles.map((article, index) => (
            <motion.article
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group bg-gradient-to-br from-zinc-900 to-black border border-gold/20 rounded-2xl overflow-hidden hover:border-gold/50 transition-all duration-500 cursor-pointer"
            >
              {/* Image */}
              <div className="relative w-full h-56 overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  width={800}
                  height={600}
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                <div className="absolute top-4 left-4 bg-gold/90 text-black px-4 py-2 rounded-full text-xs font-bold">
                  {article.category}
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-gold transition-colors duration-300 leading-tight">
                  {article.title}
                </h3>
                <p className="text-cream/70 text-sm mb-6 leading-relaxed">
                  {article.excerpt}
                </p>

                {/* Meta */}
                <div className="flex items-center justify-between pt-4 border-t border-gold/10">
                  <div>
                    <p className="text-cream text-xs font-semibold">{article.author}</p>
                    <p className="text-cream/50 text-xs">{article.date}</p>
                  </div>
                  <span className="text-gold text-xs font-semibold">{article.readTime}</span>
                </div>
              </div>

              {/* Hover Effect */}
              <div className="h-1 bg-gradient-to-r from-transparent via-gold to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </motion.article>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <a
            href="#"
            className="inline-flex items-center gap-3 text-gold hover:text-white transition-colors duration-300 text-lg font-semibold"
          >
            View All Articles
            <i className="ri-arrow-right-line text-xl"></i>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default BlogPreview;